import { chunk } from './batcher';
import { maskPhone } from './normalize';
import { BatchLookupResult, BatchProgress, DssClientOptions } from './types';

const DEFAULT_LOOKUP_TIMEOUT_MS = 8000;

export class DssClient {
  private dss: DssClientOptions['dss'];
  private batchDelayMs: number;
  private lookupTimeoutMs: number;
  private maxConcurrentBatches: number;
  private maxRateLimitRetries: number;
  private rateLimitRetryDelayMs: number;

  constructor(options: DssClientOptions) {
    this.dss = options.dss;
    this.batchDelayMs = options.batchDelayMs ?? 50;
    this.lookupTimeoutMs = options.lookupTimeoutMs ?? DEFAULT_LOOKUP_TIMEOUT_MS;
    this.maxConcurrentBatches = Math.max(1, options.maxConcurrentBatches ?? 1);
    this.maxRateLimitRetries = options.maxRateLimitRetries ?? 1;
    this.rateLimitRetryDelayMs = options.rateLimitRetryDelayMs ?? 1000;
  }

  isLookupApiAvailable(): boolean {
    const available = typeof this.dss?.lookupByPhoneNumbers === 'function';
    return available;
  }

  async lookupByPhoneNumbers(phones: string[]): Promise<BatchLookupResult> {
    if (!phones || phones.length === 0) {
      return { resultArray: [], foundArray: [], notFoundArray: [] };
    }

    if (phones.length > 5) {
      throw new Error(
        `lookupByPhoneNumbers accepts max 5 phones. Received ${phones.length}. Use lookupMany() for larger batches.`
      );
    }

    if (!this.isLookupApiAvailable()) {
      console.error('[Enrichment] DssClient: Batch API not available in SDK');
      throw new Error('Batch API (lookupByPhoneNumbers) not available in SDK');
    }

    let attempt = 0;

    while (attempt <= this.maxRateLimitRetries) {
      try {
        // Call as method to preserve 'this' context in DSS plugin
        const result = await this.withLookupTimeout(this.dss.lookupByPhoneNumbers!(phones), phones);
        return {
          resultArray: result?.resultArray || [],
          foundArray: result?.foundArray || [],
          notFoundArray: result?.notFoundArray || [],
        };
      } catch (error: any) {
        const maskedPhones = phones.map(maskPhone).join(', ');

        const isRateLimited = error?.status === 429 || error?.statusCode === 429;
        if (isRateLimited && attempt < this.maxRateLimitRetries) {
          attempt += 1;
          await this.delay(this.rateLimitRetryDelayMs);
          continue;
        }

        console.error(`[Enrichment] DssClient: DSS batch lookup failed`, error);
        return {
          resultArray: [],
          foundArray: [],
          notFoundArray: phones,
        };
      }
    }

    return { resultArray: [], foundArray: [], notFoundArray: phones };
  }

  async lookupMany(
    phones: string[],
    onProgress: (progress: BatchProgress) => void,
    signal?: AbortSignal
  ): Promise<void> {
    if (!phones || phones.length === 0) {
      return;
    }

    if (signal?.aborted) {
      throw new Error('Lookup aborted before starting');
    }

    const batches = chunk(phones, 5);
    const totalBatches = batches.length;

    let nextBatchIndex = 0;
    const runBatch = async (batchIndex: number): Promise<void> => {
      if (signal?.aborted) {
        throw new Error('Lookup aborted');
      }

      const batch = batches[batchIndex];
      let result: BatchLookupResult;

      try {
        result = await this.lookupByPhoneNumbers(batch);
      } catch (error) {
        console.error(`[Enrichment] DssClient: Batch ${batchIndex + 1}/${totalBatches} failed`, error);
        result = { resultArray: [], foundArray: [], notFoundArray: batch };
      }

      onProgress({
        batchIndex,
        totalBatches,
        phonesInBatch: batch,
        result,
      });
    };

    const workers: Promise<void>[] = [];

    const startNext = async (): Promise<void> => {
      const currentIndex = nextBatchIndex;
      if (currentIndex >= totalBatches) {
        return;
      }
      nextBatchIndex += 1;
      await runBatch(currentIndex);
      if (nextBatchIndex < totalBatches) {
        await this.delay(this.batchDelayMs);
        await startNext();
      }
    };

    const workerCount = Math.min(this.maxConcurrentBatches, totalBatches);
    for (let i = 0; i < workerCount; i += 1) {
      workers.push(startNext());
    }

    await Promise.all(workers);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Cap how long we wait on SDK batch lookup so Directory Search fallback can run
   * without blocking on the SDK's 30s Mercury timeout (BEMS01995768 delay fix).
   */
  private withLookupTimeout(
    lookupPromise: Promise<BatchLookupResult>,
    phones: string[]
  ): Promise<BatchLookupResult> {
    if (this.lookupTimeoutMs <= 0) {
      return lookupPromise;
    }

    let timedOut = false;
    const timeoutPromise = new Promise<BatchLookupResult>((resolve) => {
      setTimeout(() => {
        timedOut = true;
        resolve({
          resultArray: [],
          foundArray: [],
          notFoundArray: phones,
        });
      }, this.lookupTimeoutMs);
    });

    return Promise.race([lookupPromise, timeoutPromise]).then((result) => {
      if (timedOut) {
        console.warn(
          `[Enrichment] DssClient: batch lookup timed out after ${this.lookupTimeoutMs}ms; using fallback`
        );
      }
      return result;
    });
  }
}
